import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Row,
  Col,
  Button,
  Modal,
  Form,
  Offcanvas,
  Spinner,
} from 'react-bootstrap';
import BottomNav from '../components/BottomNav';
import TradingViewChart from '../components/TradingViewChart';
import CandlestickChart from '../components/CandlestickChart';
import axios from 'axios';
import OptionPopup from '../components/OptionPopup';
import api from '../services/api';

const timeOptions = [
  { label: '30s', reward: '30%', min: 1, duration: 30 },
  { label: '60s', reward: '40%', min: '5,000', duration: 60 },
  { label: '120s', reward: '50%', min: '10,000', duration: 120 },
  { label: '180s', reward: '60%', min: '20,000', duration: 180 },
  { label: '240s', reward: '70%', min: '30,000', duration: 240 },
  { label: '300s', reward: '80%', min: '50,000', duration: 300 },
  { label: '600s', reward: '100%', min: '100,000', duration: 600 },
];

const mapSymbolToId = (symbol) => {
  const mapping = {
    BTC: 'bitcoin',
    ETH: 'ethereum',
    XRP: 'ripple',
    BNB: 'binancecoin',
    ADA: 'cardano',
    ETC: 'ethereum-classic',
    SOL: 'solana',
    SHIB: 'shiba-inu',
    DOT: 'polkadot',
    ICP: 'internet-computer',
    ACH: 'alchemy-pay',
  };
  return mapping[symbol.toUpperCase()];
};

const UserOption = () => {
  const userOutcomeRef = useRef('Win');
  const [selectedCoin, setSelectedCoin] = useState(() => {
    const saved = localStorage.getItem('selectedOptionCoin');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  const [coinList, setCoinList] = useState([]);
  const [livePrice, setLivePrice] = useState(0);
  const [selectedTime, setSelectedTime] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [purchaseQty, setPurchaseQty] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [showCoinSelector, setShowCoinSelector] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSimulation, setShowSimulation] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [tradeResult, setTradeResult] = useState(null);
  const [direction, setDirection] = useState('Long');
  const [totalDuration, setTotalDuration] = useState(0);
  const [tradeEndTime, setTradeEndTime] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [longPercentage, setLongPercentage] = useState(33);
  const [shortPercentage, setShortPercentage] = useState(67);
  const [chartType, setChartType] = useState('candlestick'); // 'line' or 'candlestick'

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update long/short percentages every 400ms for fast, visible fluctuations
  useEffect(() => {
    const interval = setInterval(() => {
      setLongPercentage((prev) => {
        // Drift by up to ±5% each tick, clamped between 20 and 80
        const delta = Math.floor(Math.random() * 11) - 5;
        const next = Math.min(80, Math.max(20, prev + delta));
        setShortPercentage(100 - next);
        return next;
      });
    }, 400);

    return () => clearInterval(interval);
  }, []);

  // Restore active trade from localStorage on page load
  useEffect(() => {
    const savedTrade = localStorage.getItem('activeTrade');
    if (savedTrade) {
      try {
        const trade = JSON.parse(savedTrade);
        const now = Math.floor(Date.now() / 1000);
        const remaining = trade.endTime - now;

        if (remaining > 0) {
          // Restore trade state
          setShowSimulation(true);
          setTradeEndTime(trade.endTime);
          setCountdown(remaining);
          setTotalDuration(trade.totalDuration);
          setDirection(trade.direction);
          setSelectedCoin(trade.selectedCoin);
          setPurchaseQty(trade.purchaseQty);
          setSelectedTime(trade.selectedTime);
        } else {
          // Trade expired, clear it
          localStorage.removeItem('activeTrade');
        }
      } catch (error) {
        localStorage.removeItem('activeTrade');
      }
    }
  }, []);

  // Run countdown timer when trade is active
  useEffect(() => {
    if (!showSimulation || !tradeEndTime) return;

    const interval = setInterval(async () => {
      const now = Math.floor(Date.now() / 1000);
      const remaining = tradeEndTime - now;

      if (remaining <= 0) {
        clearInterval(interval);
        setCountdown(0);
        setShowSimulation(false);

        // Get trade data from localStorage
        const savedTrade = localStorage.getItem('activeTrade');
        if (savedTrade) {
          try {
            const trade = JSON.parse(savedTrade);
            localStorage.removeItem('activeTrade');

            // Show modal immediately with pending state
            setTradeResult({
              coin: trade.selectedCoin.symbol,
              change: 'Calculating...',
              resultText: 'Processing settlement',
              direction: trade.direction,
              quantity: `${trade.purchaseQty} USDT`,
              time: trade.selectedTime,
              currentPrice: 'Calculating...',
              entryPrice: 'Calculating...',
              tradeId: trade.tradeId,
              isPending: true,
            });

            // Fetch the actual trade result from API with retry logic
            const fetchTradeResult = async (
              retryCount = 0,
              maxRetries = 15,
            ) => {
              try {
                const response = await api.get(
                  `/api/admin/trading/history?all=true`,
                );

                if (response.data.success) {
                  // First check if trade exists at all (might not be COMPLETED yet)
                  const foundTrade = response.data.data.trades.find(
                    (t) => t.id === trade.tradeId,
                  );

                  if (foundTrade) {
                    // Check if it's completed
                    if (foundTrade.status === 'COMPLETED') {
                      const isWin = foundTrade.result === 'WIN';
                      const profitLoss = parseFloat(foundTrade.profitLoss);

                      // Get current price from live price or exit price
                      const currentPriceValue =
                        parseFloat(foundTrade.exitPrice) ||
                        parseFloat(foundTrade.entryPrice) ||
                        parseFloat(trade.livePrice);

                      const resultData = {
                        coin: trade.selectedCoin.symbol,
                        change: isWin
                          ? `+${Math.abs(profitLoss).toFixed(2)} USDT`
                          : `-${Math.abs(profitLoss).toFixed(2)} USDT`,
                        resultText: 'Complete maturity settlement',
                        direction: trade.direction,
                        quantity: `${trade.purchaseQty} USDT`,
                        time: trade.selectedTime,
                        currentPrice: parseFloat(currentPriceValue).toFixed(2),
                        entryPrice: parseFloat(foundTrade.entryPrice).toFixed(
                          2,
                        ),
                        tradeId: trade.tradeId,
                        result: foundTrade.result,
                        outcomeReason: foundTrade.outcomeReason,
                      };

                      setTradeResult(resultData);
                      return; // Success - exit
                    } else {
                      // Retry after delay - faster retry for backend processing
                      if (retryCount < maxRetries - 1) {
                        const delay = 1000; // Fixed 1 second delay for faster checking
                        setTimeout(
                          () => fetchTradeResult(retryCount + 1, maxRetries),
                          delay,
                        );
                      } else {
                      }
                    }
                  } else {
                    // Retry after delay
                    if (retryCount < maxRetries - 1) {
                      const delay = 1000; // Fixed 1 second delay
                      setTimeout(
                        () => fetchTradeResult(retryCount + 1, maxRetries),
                        delay,
                      );
                    } else {
                    }
                  }
                }
              } catch (error) {
                if (retryCount < maxRetries - 1) {
                  const delay = 1000;
                  setTimeout(
                    () => fetchTradeResult(retryCount + 1, maxRetries),
                    delay,
                  );
                }
              }
            };

            // Start fetching with retry logic
            try {
              fetchTradeResult(0, 15);
            } catch (apiError) {
              // Fallback to local calculation if API fails
              const success = trade.randomNumber <= trade.userWinPercentage;
              const rewardPercent =
                parseFloat(trade.rewardPercent.replace('%', '')) / 100;
              const profitAmount = success
                ? Number(trade.purchaseQty) * rewardPercent
                : 0;
              const lossAmount = success ? 0 : Number(trade.purchaseQty);

              setTradeResult({
                coin: trade.selectedCoin.symbol,
                change: success
                  ? `+${profitAmount.toFixed(2)} USDT`
                  : `-${lossAmount.toFixed(2)} USDT`,
                resultText: 'Complete maturity settlement',
                direction: trade.direction,
                quantity: `${trade.purchaseQty} USDT`,
                time: trade.selectedTime,
                currentPrice: parseFloat(trade.livePrice).toFixed(2),
                entryPrice: parseFloat(trade.entryPrice).toFixed(2),
                tradeId: trade.tradeId,
              });
            }
          } catch (error) {}
        }
      } else {
        setCountdown(remaining);
      }
    }, 100); // Update every 100ms for smooth countdown

    return () => clearInterval(interval);
  }, [showSimulation, tradeEndTime]);

  // Fetch coins from backend
  useEffect(() => {
    const fetchCoins = async () => {
      try {
        const response = await api.get('/api/trading/prices');

        const prices = response.data?.data?.prices || [];

        const formattedCoins = prices.map((coin) => ({
          symbol: `${coin.symbol}/USDT`,
          price: coin.currentPrice,
          change: coin.priceChange24h,
          name: coin.name,
        }));

        setCoinList(formattedCoins);

        // Set first coin as default if no coin is selected
        if (!selectedCoin && formattedCoins.length > 0) {
          setSelectedCoin(formattedCoins[0]);
          setLivePrice(formattedCoins[0].price);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchCoins();
    // Refresh prices every 30 seconds
    const interval = setInterval(fetchCoins, 30000);
    return () => clearInterval(interval);
  }, [selectedCoin]);

  // Save selected coin to localStorage
  useEffect(() => {
    if (selectedCoin) {
      localStorage.setItem('selectedOptionCoin', JSON.stringify(selectedCoin));
      // Update live price when coin changes
      setLivePrice(selectedCoin.price);
    }
  }, [selectedCoin]);

  useEffect(() => {
    const initialize = async () => {
      if (!selectedCoin || coinList.length === 0) return;

      try {
        const [base] = selectedCoin.symbol.split('/');
        const id = mapSymbolToId(base);
        const res = await axios.get(
          `https://api.coingecko.com/api/v3/simple/price?ids=${id}&vs_currencies=usd`,
        );
        const price = res.data[id]?.usd;
        if (price) setLivePrice(price);
      } catch (err) {}
    };
    initialize();
  }, [selectedCoin, coinList]);

  const handleOrderClick = async () => {
    const selected = timeOptions.find((t) => t.label === selectedTime);

    // Validate that time is selected
    if (!selected) {
      setErrorMsg('Please select a trading duration');
      return;
    }

    // Validate that purchase quantity is entered and greater than 0
    const qty = Number(purchaseQty);
    if (!purchaseQty || isNaN(qty) || qty <= 0) {
      setErrorMsg('Please enter a valid purchase quantity');
      return;
    }

    // Validate minimum amount for selected duration
    const minAmount =
      typeof selected.min === 'string'
        ? Number(selected.min.replace(/,/g, ''))
        : selected.min;

    if (qty < minAmount) {
      setErrorMsg(`Minimum amount for ${selected.label} is $${selected.min}`);
      return;
    }

    setErrorMsg('');
    setShowPopup(false);

    try {
      // API call to place trade
      const [base] = selectedCoin.symbol.split('/');
      const tradePayload = {
        symbol: base,
        tradeType: direction.toUpperCase(),
        amount: Number(purchaseQty),
        entryPrice: livePrice,
        durationSeconds: selected.duration,
      };

      const response = await api.post('/api/trading/place-trade', tradePayload);

      if (response.data.success) {
        const tradeData = response.data.data.trade;

        // Calculate exact end time using Unix timestamp
        const endTime = Math.floor(Date.now() / 1000) + selected.duration;

        setShowSimulation(true);
        setCountdown(selected.duration);
        setTotalDuration(selected.duration);
        setTradeEndTime(endTime);

        // Store trade in localStorage for persistence
        const tradeInfo = {
          endTime: endTime,
          totalDuration: selected.duration,
          direction: direction,
          selectedCoin: selectedCoin,
          purchaseQty: purchaseQty,
          selectedTime: selected.label,
          tradeId: tradeData.id,
          entryPrice: parseFloat(tradeData.entryPrice).toFixed(2),
          livePrice: parseFloat(livePrice).toFixed(2),
          rewardPercent: selected.reward,
          randomNumber: tradeData.randomNumber,
          userWinPercentage: tradeData.userWinPercentage,
        };
        localStorage.setItem('activeTrade', JSON.stringify(tradeInfo));

        // Use timestamp-based countdown for accuracy
        const interval = setInterval(async () => {
          const now = Math.floor(Date.now() / 1000);
          const remaining = endTime - now;

          if (remaining <= 0) {
            clearInterval(interval);
            setCountdown(0);
            setShowSimulation(false);
            localStorage.removeItem('activeTrade');

            // Show modal immediately with pending state
            setTradeResult({
              coin: selectedCoin.symbol,
              change: 'Calculating...',
              resultText: 'Processing settlement',
              direction,
              quantity: `${purchaseQty} USDT`,
              time: selected.label,
              currentPrice: 'Calculating...',
              entryPrice: 'Calculating...',
              tradeId: tradeData.id,
              isPending: true,
            });

            // Fetch the actual trade result from API with retry logic
            const fetchNewTradeResult = async (
              retryCount = 0,
              maxRetries = 15,
            ) => {
              try {
                const response = await api.get(
                  `/api/admin/trading/history?all=true`,
                );

                if (response.data.success) {
                  // First check if trade exists at all (might not be COMPLETED yet)
                  const foundTrade = response.data.data.trades.find(
                    (t) => t.id === tradeData.id,
                  );

                  if (foundTrade) {
                    if (foundTrade.status === 'COMPLETED') {
                      const isWin = foundTrade.result === 'WIN';
                      const profitLoss = parseFloat(foundTrade.profitLoss);

                      // Get current price from live price or exit price
                      const currentPriceValue =
                        parseFloat(foundTrade.exitPrice) ||
                        parseFloat(foundTrade.entryPrice) ||
                        livePrice;

                      const resultData = {
                        coin: selectedCoin.symbol,
                        change: isWin
                          ? `+${Math.abs(profitLoss).toFixed(2)} USDT`
                          : `-${Math.abs(profitLoss).toFixed(2)} USDT`,
                        resultText: 'Complete maturity settlement',
                        direction,
                        quantity: `${purchaseQty} USDT`,
                        time: selected.label,
                        currentPrice: parseFloat(currentPriceValue).toFixed(2),
                        entryPrice: parseFloat(foundTrade.entryPrice).toFixed(
                          2,
                        ),
                        tradeId: tradeData.id,
                        result: foundTrade.result,
                        outcomeReason: foundTrade.outcomeReason,
                      };

                      setTradeResult(resultData);
                      return;
                    } else {
                      // Retry after delay - faster retry for backend processing
                      if (retryCount < maxRetries - 1) {
                        const delay = 1000;
                        setTimeout(
                          () => fetchNewTradeResult(retryCount + 1, maxRetries),
                          delay,
                        );
                      } else {
                      }
                    }
                  } else {
                    // Retry after delay
                    if (retryCount < maxRetries - 1) {
                      const delay = 1000;
                      setTimeout(
                        () => fetchNewTradeResult(retryCount + 1, maxRetries),
                        delay,
                      );
                    } else {
                    }
                  }
                }
              } catch (error) {
                if (retryCount < maxRetries - 1) {
                  const delay = 1000;
                  setTimeout(
                    () => fetchNewTradeResult(retryCount + 1, maxRetries),
                    delay,
                  );
                }
              }
            };

            // Start fetching with retry logic
            try {
              fetchNewTradeResult(0, 15);
            } catch (apiError) {
              // Fallback to local calculation if API fails
              const success =
                tradeData.randomNumber <= tradeData.userWinPercentage;
              const rewardPercent =
                parseFloat(selected.reward.replace('%', '')) / 100;
              const profitAmount = success
                ? Number(purchaseQty) * rewardPercent
                : 0;
              const lossAmount = success ? 0 : Number(purchaseQty);

              setTradeResult({
                coin: selectedCoin.symbol,
                change: success
                  ? `+${profitAmount.toFixed(2)} USDT`
                  : `-${lossAmount.toFixed(2)} USDT`,
                resultText: 'Complete maturity settlement',
                direction,
                quantity: `${purchaseQty} USDT`,
                time: selected.label,
                currentPrice: parseFloat(livePrice).toFixed(2),
                entryPrice: parseFloat(tradeData.entryPrice).toFixed(2),
                tradeId: tradeData.id,
              });
            }
          } else {
            setCountdown(remaining);
          }
        }, 100);
      }
    } catch (error) {
      setErrorMsg(
        error.response?.data?.message ||
          'Failed to place trade. Please try again.',
      );
      setShowPopup(true);
    }
  };

  const filteredCoins = coinList.filter((c) =>
    c.symbol.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #ffffff 0%, #ffffff 100%)',
        paddingTop: '20px',
        paddingBottom: '100px',
      }}
    >
      <Container
        fluid
        style={{
          maxWidth: isMobile ? '100%' : '85%',
          padding: 'clamp(1rem, 3vw, 1.5rem)',
        }}
      >
        {selectedCoin && coinList.length > 0 ? (
          <>
            {/* Coin Header Card */}
            <div
              className='d-flex align-items-center justify-content-between mb-3 p-3 bg-white rounded'
              onClick={() => setShowCoinSelector(true)}
              style={{
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                border: '1px solid #e0e0e0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
              }}
            >
              <div className='d-flex align-items-center'>
                <img
                  src={require(
                    `../assets/coins/${selectedCoin.symbol
                      .split('/')[0]
                      .toLowerCase()}.png`,
                  )}
                  alt='coin'
                  style={{
                    width: 'clamp(32px, 8vw, 48px)',
                    height: 'clamp(32px, 8vw, 48px)',
                  }}
                  className='me-3'
                />
                <div>
                  <div
                    className='fw-bold'
                    style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)' }}
                  >
                    {selectedCoin.symbol}
                  </div>
                  <div
                    className='text-muted'
                    style={{ fontSize: 'clamp(0.7rem, 2vw, 0.85rem)' }}
                  >
                    Tap to change
                  </div>
                </div>
              </div>
              <div className='text-end'>
                <div
                  className='fw-bold'
                  style={{
                    fontSize: 'clamp(1.1rem, 3.5vw, 1.5rem)',
                    color: '#1a1a1a',
                  }}
                >
                  ${livePrice.toFixed(2)}
                </div>
                <div
                  className={
                    selectedCoin.change >= 0 ? 'text-success' : 'text-danger'
                  }
                  style={{
                    fontSize: 'clamp(0.8rem, 2.2vw, 1rem)',
                    fontWeight: '600',
                  }}
                >
                  {selectedCoin.change >= 0 ? '+' : ''}
                  {Number(selectedCoin.change).toFixed(2)}%
                </div>
              </div>
            </div>

            {/* Long/Short Indicator */}
            <div
              className='bg-white rounded p-3 mb-3'
              style={{
                border: '1px solid #e0e0e0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              }}
            >
              <div className='d-flex justify-content-between align-items-center mb-2'>
                <div
                  style={{ fontSize: 'clamp(0.75rem, 2vw, 0.9rem)' }}
                  className='text-success fw-bold'
                >
                  <i className='bi bi-arrow-up-circle-fill me-1'></i>
                  Long {longPercentage}%
                </div>
                <div
                  style={{ fontSize: 'clamp(0.75rem, 2vw, 0.9rem)' }}
                  className='text-danger fw-bold'
                >
                  Short {shortPercentage}%
                  <i className='bi bi-arrow-down-circle-fill ms-1'></i>
                </div>
              </div>
              <div
                style={{
                  borderRadius: '8px',
                  overflow: 'hidden',
                  height: 'clamp(6px, 2vw, 10px)',
                  display: 'flex',
                }}
              >
                <div
                  style={{
                    width: `${longPercentage}%`,
                    background: '#22c55e',
                    transition: 'width 0.35s ease-in-out',
                  }}
                ></div>
                <div
                  style={{
                    width: `${shortPercentage}%`,
                    background: '#ef4444',
                    transition: 'width 0.35s ease-in-out',
                  }}
                ></div>
              </div>
            </div>

            {/* Chart Card */}
            <div
              className='bg-white rounded p-2 mb-3'
              style={{
                minHeight: '300px',
                border: '1px solid #e0e0e0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              }}
            >
              {/* Chart Type Toggle */}
              <div className='d-flex justify-content-end mb-2 gap-2'>
                <Button
                  size='sm'
                  variant={
                    chartType === 'line' ? 'primary' : 'outline-secondary'
                  }
                  onClick={() => setChartType('line')}
                  style={{
                    fontSize: 'clamp(0.7rem, 2vw, 0.85rem)',
                    padding:
                      'clamp(0.3rem, 1.5vw, 0.5rem) clamp(0.5rem, 2vw, 0.75rem)',
                    borderRadius: '6px',
                  }}
                >
                  <i className='bi bi-graph-up me-1'></i>
                  Line
                </Button>
                <Button
                  size='sm'
                  variant={
                    chartType === 'candlestick'
                      ? 'primary'
                      : 'outline-secondary'
                  }
                  onClick={() => setChartType('candlestick')}
                  style={{
                    fontSize: 'clamp(0.7rem, 2vw, 0.85rem)',
                    padding:
                      'clamp(0.3rem, 1.5vw, 0.5rem) clamp(0.5rem, 2vw, 0.75rem)',
                    borderRadius: '6px',
                  }}
                >
                  <i className='bi bi-bar-chart me-1'></i>
                  Candlestick
                </Button>
              </div>

              {/* Conditional Chart Rendering */}
              {chartType === 'line' ? (
                <TradingViewChart
                  coinSymbol={selectedCoin.symbol.split('/')[0]}
                  height={isMobile ? 240 : 400}
                />
              ) : (
                <CandlestickChart
                  coinSymbol={selectedCoin.symbol.split('/')[0]}
                  height={isMobile ? 240 : 400}
                />
              )}
            </div>

            {/* Action Buttons */}
            <Row className='g-3 mb-4'>
              <Col xs={6}>
                <Button
                  variant='success'
                  className='w-100 fw-bold shadow-sm'
                  style={{
                    padding: 'clamp(0.7rem, 2.5vw, 1rem)',
                    fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
                    borderRadius: '10px',
                    border: 'none',
                    background: '#198754',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                  }}
                  onClick={() => {
                    setDirection('Long');
                    setShowPopup(true);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow =
                      '0 6px 20px rgba(34, 197, 94, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow =
                      '0 2px 8px rgba(0,0,0,0.1)';
                  }}
                >
                  <i className='bi bi-arrow-up-circle me-2'></i>
                  Long
                </Button>
              </Col>
              <Col xs={6}>
                <Button
                  variant='danger'
                  className='w-100 fw-bold shadow-sm'
                  style={{
                    padding: 'clamp(0.7rem, 2.5vw, 1rem)',
                    fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
                    borderRadius: '10px',
                    border: 'none',
                    background: '#DC3545',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                  }}
                  onClick={() => {
                    setDirection('Short');
                    setShowPopup(true);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow =
                      '0 6px 20px rgba(239, 68, 68, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow =
                      '0 2px 8px rgba(0,0,0,0.1)';
                  }}
                >
                  <i className='bi bi-arrow-down-circle me-2'></i>
                  Short
                </Button>
              </Col>
            </Row>
          </>
        ) : (
          <div className='text-center mt-5'>
            <Spinner
              animation='border'
              variant='primary'
              style={{ width: '3rem', height: '3rem' }}
            />
            <p className='mt-3 text-muted'>Loading coins...</p>
          </div>
        )}
      </Container>

      <BottomNav />

      {/* Coin Selector */}
      <Offcanvas
        show={showCoinSelector}
        onHide={() => setShowCoinSelector(false)}
        placement='end'
        style={{ width: isMobile ? '85%' : '400px' }}
      >
        <Offcanvas.Header
          closeButton
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
          }}
        >
          <Offcanvas.Title
            style={{ fontSize: 'clamp(1rem, 3vw, 1.3rem)', fontWeight: '600' }}
          >
            Select Coin
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body style={{ background: '#f8f9fa' }}>
          <div className='position-relative mb-3'>
            <i
              className='bi bi-search position-absolute'
              style={{
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#6c757d',
                fontSize: '1.1rem',
              }}
            ></i>
            <Form.Control
              type='text'
              placeholder='Search coin...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                paddingLeft: '40px',
                borderRadius: '10px',
                border: '2px solid #e9ecef',
                fontSize: 'clamp(0.85rem, 2.5vw, 1rem)',
              }}
            />
          </div>
          {filteredCoins.map((coin, idx) => (
            <div
              key={idx}
              className='d-flex justify-content-between align-items-center p-3 mb-2 bg-white rounded shadow-sm'
              style={{
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              onClick={() => {
                setSelectedCoin(coin);
                setShowCoinSelector(false);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateX(5px)';
                e.currentTarget.style.boxShadow =
                  '0 4px 12px rgba(102, 126, 234, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateX(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
              }}
            >
              <div>
                <strong style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1.05rem)' }}>
                  {coin.symbol}
                </strong>
                <br />
                <small
                  className='text-muted'
                  style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}
                >
                  ${coin.price.toFixed(2)}
                </small>
              </div>
              <div
                className={coin.change >= 0 ? 'text-success' : 'text-danger'}
                style={{
                  fontSize: 'clamp(0.85rem, 2.5vw, 1rem)',
                  fontWeight: '600',
                }}
              >
                {coin.change >= 0 ? '+' : ''}
                {Number(coin.change).toFixed(2)}%
              </div>
            </div>
          ))}
        </Offcanvas.Body>
      </Offcanvas>

      {/* Trade Confirmation Modal */}
      <Modal
        show={showPopup}
        onHide={() => setShowPopup(false)}
        centered
        size={isMobile ? '' : 'lg'}
      >
        <Modal.Header
          closeButton
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
          }}
        >
          <Modal.Title style={{ fontSize: 'clamp(1rem, 3vw, 1.3rem)' }}>
            {selectedCoin?.symbol}
            <span className='ms-2' style={{ color: '#f8f9fb' }}>
              {selectedTime || 'Select Duration'}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{ background: '#f8f9fa', padding: 'clamp(1rem, 3vw, 1.5rem)' }}
        >
          <div className='mb-3'>
            <h6
              className='mb-3 text-center'
              style={{
                fontSize: 'clamp(0.85rem, 2.5vw, 1rem)',
                color: '#6c757d',
                fontWeight: '600',
              }}
            >
              Select Trading Duration
            </h6>
            <Row className='g-2 justify-content-center'>
              {timeOptions.map((t) => (
                <Col key={t.label} xs={6} sm={4} className='text-center'>
                  <Button
                    className='w-100 fw-bold shadow-sm'
                    style={{
                      background:
                        selectedTime === t.label ? '#198754' : '#E57373',
                      border: 'none',
                      borderRadius: '10px',
                      padding: 'clamp(0.7rem, 2.5vw, 1rem)',
                      fontSize: 'clamp(0.8rem, 2.2vw, 0.95rem)',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      transform:
                        selectedTime === t.label ? 'scale(1.05)' : 'scale(1)',
                    }}
                    onClick={() => {
                      setSelectedTime(t.label);
                      setErrorMsg('');
                    }}
                    onMouseEnter={(e) => {
                      if (selectedTime !== t.label) {
                        e.currentTarget.style.transform = 'scale(1.03)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedTime !== t.label) {
                        e.currentTarget.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    <div style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1.05rem)' }}>
                      {t.label}
                    </div>
                    <div
                      style={{
                        fontSize: 'clamp(0.75rem, 2vw, 0.85rem)',
                        marginTop: '4px',
                      }}
                    >
                      {t.reward}
                    </div>
                  </Button>
                </Col>
              ))}
            </Row>
          </div>

          <Form.Group className='mb-3'>
            <Form.Label
              className='fw-bold'
              style={{ fontSize: 'clamp(0.85rem, 2.5vw, 1rem)' }}
            >
              Purchase Quantity
              <span
                className='text-danger ms-2'
                style={{ fontSize: 'clamp(0.75rem, 2vw, 0.9rem)' }}
              >
                (Min: $
                {timeOptions.find((t) => t.label === selectedTime)?.min || 1})
              </span>
            </Form.Label>
            <div className='position-relative'>
              <span
                className='position-absolute'
                style={{
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#6c757d',
                  fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
                  fontWeight: '600',
                }}
              >
                $
              </span>
              <Form.Control
                type='number'
                value={purchaseQty}
                onChange={(e) => {
                  setPurchaseQty(e.target.value);
                  setErrorMsg('');
                }}
                placeholder='Enter amount'
                style={{
                  paddingLeft: '30px',
                  borderRadius: '10px',
                  border: '2px solid #e9ecef',
                  fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)',
                  padding:
                    'clamp(0.6rem, 2vw, 0.8rem) clamp(0.6rem, 2vw, 0.8rem) clamp(0.6rem, 2vw, 0.8rem) 30px',
                }}
              />
            </div>
          </Form.Group>

          {errorMsg && (
            <div
              className='alert alert-danger d-flex align-items-center'
              style={{
                fontSize: 'clamp(0.8rem, 2.2vw, 0.9rem)',
                borderRadius: '10px',
              }}
            >
              <i className='bi bi-exclamation-triangle-fill me-2'></i>
              {errorMsg}
            </div>
          )}

          <Button
            className='w-100 fw-bold shadow'
            style={{
              background: '#198754',
              border: 'none',
              borderRadius: '10px',
              padding: 'clamp(0.7rem, 2.5vw, 1rem)',
              fontSize: 'clamp(0.95rem, 2.8vw, 1.15rem)',
              transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onClick={handleOrderClick}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow =
                '0 8px 24px rgba(34, 197, 94, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
            }}
          >
            <i className='bi bi-check-circle me-2'></i>
            Confirm Order
          </Button>
        </Modal.Body>
      </Modal>

      {showSimulation && (
        <OptionPopup
          initialDuration={totalDuration}
          countdown={countdown}
          direction={direction}
          selectedCoin={selectedCoin}
          purchaseQty={purchaseQty}
          entryPrice={livePrice}
          selectedTimeOption={timeOptions.find((t) => t.label === selectedTime)}
          onComplete={() => {
            setShowSimulation(false);
            const outcome = userOutcomeRef.current || 'Win';
            const success = outcome === 'Win';
            setTradeResult({
              coin: selectedCoin.symbol,
              change: success
                ? `+${purchaseQty * 0.3} USDT`
                : `-${purchaseQty} USDT`,
              resultText: 'Complete maturity settlement',
              direction,
              quantity: `${purchaseQty} USDT`,
              time: selectedTime,
              currentPrice: selectedCoin.price,
              entryPrice: (selectedCoin.price - 20).toFixed(2),
            });
          }}
        />
      )}

      {/* Result Popup */}
      {tradeResult && (
        <Modal
          show
          centered
          onHide={() => !tradeResult.isPending && setTradeResult(null)}
          backdrop={tradeResult.isPending ? 'static' : true}
          keyboard={!tradeResult.isPending}
          size={isMobile ? '' : 'md'}
        >
          <Modal.Body
            className='text-center'
            style={{
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              padding: 'clamp(1.5rem, 4vw, 2.5rem)',
              borderRadius: '15px',
            }}
          >
            <div className='mb-3'>
              <h5
                className='fw-bold'
                style={{
                  fontSize: 'clamp(1.2rem, 3.5vw, 1.5rem)',
                  color: '#1a1a1a',
                }}
              >
                {tradeResult.coin}
              </h5>
            </div>

            <div
              className={
                tradeResult.isPending
                  ? ''
                  : tradeResult.change.includes('+')
                    ? 'bg-success'
                    : 'bg-danger'
              }
              style={{
                color: 'white',
                borderRadius: '15px',
                padding: 'clamp(1.5rem, 4vw, 2rem)',
                marginBottom: '1.5rem',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                background: tradeResult.isPending ? '#667eea' : undefined,
              }}
            >
              {tradeResult.isPending ? (
                <>
                  <Spinner animation='border' className='mb-2' />
                  <h3
                    className='fw-bold mb-2'
                    style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)' }}
                  >
                    {tradeResult.change}
                  </h3>
                  <div style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)' }}>
                    {tradeResult.resultText}
                  </div>
                </>
              ) : (
                <>
                  <h3
                    className='fw-bold mb-2'
                    style={{ fontSize: 'clamp(1.5rem, 4vw, 2rem)' }}
                  >
                    {tradeResult.change}
                  </h3>
                  <div style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1.1rem)' }}>
                    {tradeResult.resultText}
                  </div>
                </>
              )}
            </div>

            <div className='bg-white rounded shadow-sm p-3 mb-3'>
              <Row className='g-2'>
                <Col xs={6} className='text-start'>
                  <small
                    className='text-muted'
                    style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}
                  >
                    Current Price
                  </small>
                  <div
                    className='fw-bold'
                    style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1.05rem)' }}
                  >
                    ${tradeResult.currentPrice}
                  </div>
                </Col>
                <Col xs={6} className='text-start'>
                  <small
                    className='text-muted'
                    style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}
                  >
                    Entry Price
                  </small>
                  <div
                    className='fw-bold'
                    style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1.05rem)' }}
                  >
                    ${tradeResult.entryPrice}
                  </div>
                </Col>
                <Col xs={6} className='text-start'>
                  <small
                    className='text-muted'
                    style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}
                  >
                    Direction
                  </small>
                  <div
                    className={`fw-bold ${
                      tradeResult.direction === 'Long'
                        ? 'text-success'
                        : 'text-danger'
                    }`}
                    style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1.05rem)' }}
                  >
                    {tradeResult.direction}
                  </div>
                </Col>
                <Col xs={6} className='text-start'>
                  <small
                    className='text-muted'
                    style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}
                  >
                    Period
                  </small>
                  <div
                    className='fw-bold'
                    style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1.05rem)' }}
                  >
                    {tradeResult.time}
                  </div>
                </Col>
                <Col xs={12} className='text-start'>
                  <small
                    className='text-muted'
                    style={{ fontSize: 'clamp(0.75rem, 2vw, 0.85rem)' }}
                  >
                    Quantity
                  </small>
                  <div
                    className='fw-bold'
                    style={{ fontSize: 'clamp(0.9rem, 2.5vw, 1.05rem)' }}
                  >
                    {tradeResult.quantity}
                  </div>
                </Col>
              </Row>
            </div>

            {!tradeResult.isPending && (
              <Button
                className='w-100 fw-bold shadow'
                style={{
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '10px',
                  padding: 'clamp(0.7rem, 2.5vw, 1rem)',
                  fontSize: 'clamp(0.95rem, 2.8vw, 1.15rem)',
                  transition: 'transform 0.2s',
                }}
                onClick={() => {
                  setTradeResult(null);
                  setSelectedTime(null);
                  setPurchaseQty('');
                  setErrorMsg('');
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.transform = 'translateY(-2px)')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = 'translateY(0)')
                }
              >
                <i className='bi bi-check-circle me-2'></i>
                Close
              </Button>
            )}
          </Modal.Body>
        </Modal>
      )}

      <BottomNav />
    </div>
  );
};

export default UserOption;
